(function () {
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const setupRevealAnimations = () => {
    const items = document.querySelectorAll('[data-reveal]');
    items.forEach((item, index) => {
      item.classList.add('reveal');
      item.style.transitionDelay = prefersReduced ? '0ms' : `${Math.min(index * 55, 380)}ms`;
    });

    if (prefersReduced || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });

    items.forEach((item) => observer.observe(item));
  };

  const setupImageLoading = () => {
    const images = document.querySelectorAll('.tws-media-image, .tws-doc-preview');
    images.forEach((img) => {
      const markLoaded = () => {
        img.classList.remove('is-pending');
        img.classList.add('is-loaded');
        img.style.opacity = '1';
        img.style.visibility = 'visible';
        img.style.filter = 'none';
        img.style.transform = 'none';
      };

      // Do not hide images while waiting for JS/image events. Older iOS Safari and
      // some content blockers can miss lazy-load/load callbacks, so images must remain visible.
      img.classList.remove('is-pending');

      if (img.complete) {
        markLoaded();
      } else {
        img.addEventListener('load', markLoaded, { once: true });
        img.addEventListener('error', markLoaded, { once: true });
        window.setTimeout(markLoaded, 1800);
      }
    });
  };

  const setupLightbox = () => {
    const triggers = document.querySelectorAll('[data-lightbox-src]');
    if (!triggers.length) return;

    const lightbox = document.createElement('div');
    lightbox.className = 'tws-lightbox';
    lightbox.hidden = true;
    lightbox.innerHTML = `
      <div class="tws-lightbox__backdrop" data-lightbox-close></div>
      <div class="tws-lightbox__dialog" role="dialog" aria-modal="true" aria-label="Expanded image preview">
        <button class="tws-lightbox__close" type="button" data-lightbox-close aria-label="Close image preview">×</button>
        <img class="tws-lightbox__image" src="" alt="">
        <div class="tws-lightbox__caption"></div>
      </div>
    `;
    document.body.appendChild(lightbox);

    const image = lightbox.querySelector('.tws-lightbox__image');
    const caption = lightbox.querySelector('.tws-lightbox__caption');

    const close = () => {
      lightbox.hidden = true;
      document.body.classList.remove('drawer-open');
      image.src = '';
      image.alt = '';
      caption.textContent = '';
    };

    triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        image.src = trigger.getAttribute('data-lightbox-src') || '';
        image.alt = trigger.getAttribute('data-lightbox-alt') || '';
        caption.textContent = trigger.closest('figure')?.querySelector('figcaption')?.textContent || trigger.getAttribute('data-lightbox-alt') || '';
        lightbox.hidden = false;
        document.body.classList.add('drawer-open');
      });
    });

    lightbox.querySelectorAll('[data-lightbox-close]').forEach((el) => el.addEventListener('click', close));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !lightbox.hidden) close();
    });
  };

  const setupDrawer = () => {
    const drawer = document.querySelector('.js-drawer');
    const toggle = document.querySelector('.js-drawer-toggle');
    const closes = document.querySelectorAll('.js-drawer-close');
    if (!drawer || !toggle) return;

    const openDrawer = () => {
      drawer.hidden = false;
      document.body.classList.add('drawer-open');
      toggle.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => drawer.classList.add('is-open'));
    };

    const closeDrawer = () => {
      drawer.classList.remove('is-open');
      document.body.classList.remove('drawer-open');
      toggle.setAttribute('aria-expanded', 'false');
      window.setTimeout(() => {
        if (!drawer.classList.contains('is-open')) drawer.hidden = true;
      }, 420);
    };

    toggle.addEventListener('click', () => {
      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      expanded ? closeDrawer() : openDrawer();
    });

    closes.forEach((el) => el.addEventListener('click', closeDrawer));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer();
    });
    drawer.querySelectorAll('a').forEach((link) => link.addEventListener('click', closeDrawer));
  };

  const setupDesktopSubmenus = () => {
    const items = document.querySelectorAll('.tws-nav__item--has-children');
    if (!items.length) return;

    items.forEach((item) => {
      const button = item.querySelector('.js-submenu-toggle');
      const menu = item.querySelector('.tws-nav__dropdown');
      if (!button || !menu) return;
      menu.hidden = true;
      let closeTimer;

      const openThis = () => {
        window.clearTimeout(closeTimer);
        items.forEach((other) => {
          if (other !== item) {
            const otherButton = other.querySelector('.js-submenu-toggle');
            const otherMenu = other.querySelector('.tws-nav__dropdown');
            other.classList.remove('is-open');
            if (otherButton) otherButton.setAttribute('aria-expanded', 'false');
            if (otherMenu) otherMenu.hidden = true;
          }
        });
        item.classList.add('is-open');
        button.setAttribute('aria-expanded', 'true');
        menu.hidden = false;
      };

      const closeThis = () => {
        window.clearTimeout(closeTimer);
        item.classList.remove('is-open');
        button.setAttribute('aria-expanded', 'false');
        menu.hidden = true;
      };

      const scheduleClose = () => {
        window.clearTimeout(closeTimer);
        closeTimer = window.setTimeout(() => {
          if (!item.matches(':hover') && !item.contains(document.activeElement)) closeThis();
        }, 340);
      };

      button.addEventListener('click', (event) => {
        event.preventDefault();
        const expanded = button.getAttribute('aria-expanded') === 'true';
        expanded ? closeThis() : openThis();
      });

      item.addEventListener('mouseenter', () => {
        if (window.innerWidth > 900) openThis();
      });
      item.addEventListener('mouseleave', () => {
        if (window.innerWidth > 900) scheduleClose();
      });
      menu.addEventListener('mouseenter', () => window.clearTimeout(closeTimer));
      menu.addEventListener('mouseleave', () => {
        if (window.innerWidth > 900) scheduleClose();
      });
      item.addEventListener('focusin', openThis);
      item.addEventListener('focusout', () => {
        window.setTimeout(() => {
          if (!item.contains(document.activeElement)) closeThis();
        }, 10);
      });
    });

    document.addEventListener('click', (event) => {
      if (!event.target.closest('.tws-nav__item--has-children')) {
        items.forEach((item) => {
          const button = item.querySelector('.js-submenu-toggle');
          const menu = item.querySelector('.tws-nav__dropdown');
          item.classList.remove('is-open');
          if (button) button.setAttribute('aria-expanded', 'false');
          if (menu) menu.hidden = true;
        });
      }
    });
  };


  const setupResponsiveNavBalance = () => {
    const nav = document.querySelector('.tws-nav');
    const menu = nav?.querySelector('.tws-nav__menu--desktop');
    if (!nav || !menu) return;

    const apply = () => {
      nav.classList.remove('tws-nav--compact', 'tws-nav--supercompact');
      if (window.innerWidth <= 900) return;

      const navWidth = nav.clientWidth;
      const brand = nav.querySelector('.tws-nav__brand');
      const menuWidth = menu.scrollWidth;
      const brandWidth = brand ? brand.getBoundingClientRect().width : 0;
      const requiredWidth = brandWidth + menuWidth + 56;

      if (requiredWidth > navWidth) {
        nav.classList.add('tws-nav--compact');
      }
      if (requiredWidth > navWidth + 140) {
        nav.classList.add('tws-nav--supercompact');
      }
    };

    apply();
    window.addEventListener('resize', apply);
    window.setTimeout(apply, 150);
  };

  const setupTracingSvg = (svg) => {
    const paths = svg ? Array.from(svg.querySelectorAll('.tws-trace-path')) : [];
    paths.forEach((path) => {
      const length = path.getTotalLength();
      path.style.strokeDasharray = `${length}`;
      path.style.strokeDashoffset = `${length}`;
    });
    return paths;
  };

  const setupNavBrandIntro = () => {
    const brand = document.querySelector('.tws-nav__brand-lockup');
    if (!brand) return;

    const letterL = brand.querySelector('.js-nav-letter-l');
    const letterW = brand.querySelector('.js-nav-letter-w');
    const birdWrap = brand.querySelector('.js-nav-bird-wrap');
    const birdSvg = birdWrap?.querySelector('svg');
    const birdPaths = setupTracingSvg(birdSvg);

    if (prefersReduced || typeof window.gsap === 'undefined') {
      [letterL, letterW, birdWrap].filter(Boolean).forEach((el) => {
        el.style.opacity = '1';
        el.style.visibility = 'visible';
        el.style.transform = 'none';
      });
      birdSvg?.classList.add('is-drawn');
      return;
    }

    window.gsap.set([letterL, letterW].filter(Boolean), { autoAlpha: 0, y: -10 });
    if (birdWrap) window.gsap.set(birdWrap, { autoAlpha: 0, x: -6, y: 0 });

    const tl = window.gsap.timeline({ defaults: { ease: 'power2.out' }, delay: 0.04 });
    if (letterL) tl.to(letterL, { autoAlpha: 1, y: 0, duration: 0.38 });
    if (letterW) tl.to(letterW, { autoAlpha: 1, y: 0, duration: 0.38 }, '-=0.16');
    if (birdWrap) {
      tl.to(birdWrap, { autoAlpha: 1, x: 0, duration: 0.24 }, '-=0.02');
      if (birdPaths.length) {
        tl.to(birdPaths, {
          strokeDashoffset: 0,
          duration: 0.78,
          stagger: 0.035,
          ease: 'power2.out'
        }, '<');
        tl.add(() => birdSvg?.classList.add('is-drawn'));
      }
    }
  };

  const setupNavMenuIntro = () => {
    const navItems = Array.from(document.querySelectorAll('.js-nav-menu .js-nav-item'));
    if (!navItems.length) return;

    if (prefersReduced || typeof window.gsap === 'undefined') {
      navItems.forEach((el) => {
        el.style.opacity = '1';
        el.style.visibility = 'visible';
        el.style.transform = 'none';
      });
      return;
    }

    window.gsap.set(navItems, { autoAlpha: 0, y: -8 });
    window.gsap.to(navItems, { autoAlpha: 1, y: 0, duration: 0.42, stagger: 0.05, ease: 'power2.out', delay: 0.18 });
  };

  const setupHeroIntro = () => {
    const hero = document.querySelector('.js-hero-intro');
    if (!hero) return;

    document.documentElement.classList.add('hero-intro-ready');

    const eyebrow = hero.querySelector('.js-hero-eyebrow');
    const intro = hero.querySelector('.js-hero-from');
    const markWrap = hero.querySelector('.js-hero-mark');
    const logoWrap = hero.querySelector('.js-hero-logo');
    const tagline = hero.querySelector('.js-hero-tagline');
    const rule = hero.querySelector('.js-hero-rule');
    const descriptor = hero.querySelector('.js-hero-descriptor');
    const link = hero.querySelector('.js-hero-link');

    const title = hero.querySelector('.js-hero-title');
    const textEls = [eyebrow, intro, title, tagline, rule, descriptor, link].filter(Boolean);
    const markSvg = markWrap?.querySelector('svg');
    const logoSvg = logoWrap?.querySelector('svg');
    const markPaths = setupTracingSvg(markSvg);
    const logoPaths = setupTracingSvg(logoSvg);
    const isHomepage = hero.classList.contains('hero-center--homepage');

    if (!isHomepage) {
      hero.classList.add('is-ready');
      const reveal = () => {
        textEls.forEach((el, index) => {
          window.setTimeout(() => el.classList.add('is-visible'), index * 90);
        });
      };
      if (prefersReduced || typeof window.gsap === 'undefined') {
        reveal();
        return;
      }
      requestAnimationFrame(reveal);
      return;
    }

    if (prefersReduced || typeof window.gsap === 'undefined') {
      hero.querySelectorAll('.hero-center__anim').forEach((el) => {
        el.style.opacity = '1';
        el.style.visibility = 'visible';
        el.style.transform = 'none';
      });
      [markSvg, logoSvg].forEach((svg) => svg?.classList.add('is-drawn'));
      return;
    }

    window.gsap.set(textEls, { autoAlpha: 0, y: 18 });
    if (markWrap) window.gsap.set(markWrap, { autoAlpha: 0, y: 14 });
    if (logoWrap) window.gsap.set(logoWrap, { autoAlpha: 0, y: 14 });

    const tl = window.gsap.timeline({ defaults: { ease: 'power2.out' }, delay: 0.08 });

    if (eyebrow) tl.to(eyebrow, { autoAlpha: 1, y: 0, duration: 0.72 });
    if (intro) tl.to(intro, { autoAlpha: 1, y: 0, duration: 0.78 }, '-=0.52');

    if (markWrap) {
      tl.to(markWrap, { autoAlpha: 1, y: 0, duration: 0.34 }, '-=0.22');
      if (markPaths.length) {
        tl.to(markPaths, {
          strokeDashoffset: 0,
          duration: 1.15,
          stagger: 0.07,
          ease: 'power2.out'
        }, '<');
        tl.add(() => markSvg?.classList.add('is-drawn'));
      }
    }

    if (logoWrap) {
      tl.to(logoWrap, { autoAlpha: 1, y: 0, duration: 0.38 }, '-=0.28');
      if (logoPaths.length) {
        tl.to(logoPaths, {
          strokeDashoffset: 0,
          duration: 1.35,
          stagger: 0.012,
          ease: 'power2.out'
        }, '<');
        tl.add(() => logoSvg?.classList.add('is-drawn'));
      }
    }

    [tagline, rule, descriptor, link].filter(Boolean).forEach((el, index) => {
      tl.to(el, { autoAlpha: 1, y: 0, duration: 0.7 }, index === 0 ? '-=0.22' : '-=0.46');
    });
  };


  const setupAnalyticsTracking = () => {
    if (window.__twsAnalyticsTrackingReady) return;
    window.__twsAnalyticsTrackingReady = true;
    window.dataLayer = window.dataLayer || [];

    const clean = (value, max = 240) => String(value || '').replace(/\s+/g, ' ').trim().slice(0, max);

    const pushEvent = (eventName, params = {}) => {
      const payload = {
        event: eventName,
        page_title: document.title,
        page_location: window.location.href,
        page_path: window.location.pathname,
        ...params
      };
      window.dataLayer.push(payload);
    };

    const classifyLink = (href, link) => {
      const lowerHref = String(href || '').toLowerCase();
      const text = clean(link?.innerText || link?.getAttribute('aria-label') || link?.getAttribute('title') || '');
      const label = `${lowerHref} ${text}`.toLowerCase();

      if (lowerHref.startsWith('tel:')) return 'phone';
      if (lowerHref.startsWith('mailto:')) return 'email';
      if (label.includes('instagram') || label.includes('facebook') || label.includes('tiktok') || label.includes('pinterest')) return 'social';
      if (label.includes('maps.google') || label.includes('google.com/maps') || label.includes('201 bear creek') || label.includes('address') || label.includes('directions')) return 'address';
      if (lowerHref && !lowerHref.startsWith('#')) {
        try {
          const url = new URL(lowerHref, window.location.href);
          if (url.hostname && url.hostname !== window.location.hostname) return 'outbound';
        } catch (error) {}
      }
      if ((link?.className || '').toString().toLowerCase().includes('button') || link?.closest('[class*="button"], [class*="cta"]')) return 'cta';
      return 'internal';
    };

    const getLinkDomain = (href) => {
      try { return new URL(href, window.location.href).hostname; }
      catch (error) { return ''; }
    };

    const getFormType = (form) => {
      if (!form) return 'unknown';
      if (form.matches('.js-tws-newsletter-form, .js-tws-event-form, .js-lw-newsletter-form, .newsletter-form, .customer-form') || form.querySelector('input[name="contact[accepts_marketing]"], input[name="contact[tags]"][value*="newsletter"]')) return 'newsletter';
      if (form.matches('.js-tws-contact-form, .js-lw-contact-form, .tws-contact-form') || form.querySelector('textarea[name="contact[body]"], input[name="contact[name]"]')) return 'contact';
      return 'generic';
    };

    const getFormLocation = (form) => {
      if (!form) return 'unknown';
      if (form.closest('.event-popup')) return 'event-popup';
      if (form.closest('.sale-popup')) return 'sale-popup';
      if (form.closest('.site-footer, footer')) return 'footer';
      if (form.closest('.social-card')) return 'social-signup';
      if (form.closest('.contact-section')) return 'contact-page';
      return clean(form.closest('section')?.className || form.className || 'form', 120);
    };

    document.querySelectorAll('[data-lw-form-success], [data-lw-form-error]').forEach((status) => {
      const form = status.closest('form');
      const formType = status.getAttribute('data-lw-form-success') || status.getAttribute('data-lw-form-error') || getFormType(form);
      const params = {
        form_type: formType,
        form_location: getFormLocation(form),
        form_id: form?.id || '',
        form_classes: clean(form?.className || '', 160)
      };
      if (status.hasAttribute('data-lw-form-success')) {
        pushEvent('lw_form_success', params);
        if (formType === 'newsletter') pushEvent('lw_newsletter_signup_success', params);
        if (formType === 'contact') pushEvent('lw_contact_form_success', params);
      } else {
        pushEvent('lw_form_error', params);
      }
    });

    // API forms report success only after an accepted response; attempts are separate.
    document.addEventListener('tws:success', (event) => {
      const form = event.target;
      if (!form?.matches?.('form')) return;
      const formType = getFormType(form);
      const params = {
        form_type: formType,
        form_location: getFormLocation(form),
        form_id: form.id || '',
        campaign_id: clean(form.dataset.twsSource || '', 100),
        signup_status: formType === 'newsletter' ? 'confirmation_requested' : 'accepted'
      };
      pushEvent('lw_form_success', params);
      if (formType === 'newsletter') pushEvent('lw_newsletter_signup_success', params);
      if (formType === 'contact') pushEvent('lw_contact_form_success', params);
    });

    document.addEventListener('focusin', (event) => {
      const form = event.target.closest?.('form');
      if (!form || form.dataset.lwFormStarted === 'true') return;
      form.dataset.lwFormStarted = 'true';
      const formType = getFormType(form);
      pushEvent('lw_form_start', {
        form_type: formType,
        form_location: getFormLocation(form),
        form_id: form.id || '',
        form_classes: clean(form.className || '', 160)
      });
      if (formType === 'newsletter') pushEvent('lw_newsletter_form_start', { form_type: formType, form_location: getFormLocation(form) });
      if (formType === 'contact') pushEvent('lw_contact_form_start', { form_type: formType, form_location: getFormLocation(form) });
    }, true);

    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (!form || !form.matches?.('form')) return;
      const formType = getFormType(form);
      const params = {
        form_type: formType,
        form_location: getFormLocation(form),
        form_id: form.id || '',
        form_classes: clean(form.className || '', 160)
      };
      pushEvent('lw_form_submit_attempt', params);
      if (formType === 'newsletter') pushEvent('lw_newsletter_submit_attempt', params);
      if (formType === 'contact') pushEvent('lw_contact_form_submit_attempt', params);
    }, true);

    document.addEventListener('click', (event) => {
      const target = event.target;
      if (!target || !target.closest) return;

      const img = target.closest('img');
      const link = target.closest('a');
      const button = target.closest('button, [role="button"], input[type="button"], input[type="submit"]');

      if (img) {
        pushEvent('lw_image_click', {
          image_alt: clean(img.getAttribute('alt') || ''),
          image_title: clean(img.getAttribute('title') || ''),
          image_src: img.currentSrc || img.src || '',
          link_url: link?.href || '',
          link_text: clean(link?.innerText || link?.getAttribute('aria-label') || ''),
          click_section: clean(img.closest('section')?.className || '', 120)
        });
      }

      if (link) {
        const href = link.href || link.getAttribute('href') || '';
        const linkType = classifyLink(href, link);
        const params = {
          click_type: linkType,
          link_text: clean(link.innerText || link.getAttribute('aria-label') || link.getAttribute('title') || ''),
          link_url: href,
          link_domain: getLinkDomain(href),
          link_id: link.id || '',
          link_classes: clean(link.className || '', 160),
          link_target: link.target || '',
          click_section: clean(link.closest('section, header, footer')?.className || '', 120)
        };
        pushEvent('lw_link_click', params);
        if (linkType === 'phone') pushEvent('lw_phone_click', params);
        if (linkType === 'email') pushEvent('lw_email_click', params);
        if (linkType === 'social') pushEvent('lw_social_click', params);
        if (linkType === 'address') pushEvent('lw_address_click', params);
        if (linkType === 'cta') pushEvent('lw_cta_click', params);
        if (linkType === 'outbound') pushEvent('lw_outbound_click', params);
      } else if (button) {
        pushEvent('lw_button_click', {
          button_text: clean(button.innerText || button.value || button.getAttribute('aria-label') || ''),
          button_id: button.id || '',
          button_classes: clean(button.className || '', 160),
          click_section: clean(button.closest('section, header, footer')?.className || '', 120)
        });
      }
    }, true);
  };

  setupAnalyticsTracking();
  setupRevealAnimations();
  setupImageLoading();
  setupLightbox();
  setupDrawer();
  setupDesktopSubmenus();
  setupResponsiveNavBalance();
  setupNavBrandIntro();
  setupNavMenuIntro();
  setupHeroIntro();

  document.addEventListener('shopify:section:load', () => {
    setupImageLoading();
  });
})();
